import ls from "local-storage";

export const normalizeRoleNames = (role) => {
  if (!role) {
    return [];
  }

  if (typeof role === "string") {
    const normalizedRole = role.trim();
    return normalizedRole ? [normalizedRole] : [];
  }

  if (Array.isArray(role)) {
    return role
      .flatMap((entry) => normalizeRoleNames(entry))
      .filter((roleName, index, allRoles) => allRoles.indexOf(roleName) === index);
  }

  if (typeof role === "object" && typeof role.name === "string") {
    const normalizedRole = role.name.trim();
    return normalizedRole ? [normalizedRole] : [];
  }

  return [];
};

export const getRoleNamesFromUser = (user) => {
  if (!user || !Array.isArray(user.roles)) {
    return [];
  }

  return user.roles
    .flatMap((role) => normalizeRoleNames(role));
};

export const getPrimaryRoleFromUser = (user) => getRoleNamesFromUser(user)[0] || "";

export const getStoredRoleNames = () => {
  const storedRoles = normalizeRoleNames(ls.get("roles"));
  if (storedRoles.length > 0) {
    return storedRoles;
  }

  return getRoleNamesFromUser(ls.get("user"));
};

export const persistStoredRole = (role) => {
  const normalizedRole = normalizeRoleNames(role)[0] || false;

  ls.set("roles", normalizedRole);

  if (typeof window !== "undefined") {
    if (normalizedRole) {
      window.localStorage.setItem("roles", normalizedRole);
    } else {
      window.localStorage.removeItem("roles");
    }
  }

  return normalizedRole;
};

export const persistStoredUser = (user) => {
  ls.set("user", user || false);
  return user || false;
};

export const syncRoleToggleEligibility = ({ user, role } = {}) => {
  const roleNames = getRoleNamesFromUser(user);
  const currentEligibility = ls.get("roleToggleEligible") === true;
  const nextEligibility =
    currentEligibility ||
    role === "Super Admin" ||
    roleNames.includes("Super Admin") ||
    Number(user?.is_role_toggled) === 1;

  ls.set("roleToggleEligible", nextEligibility);

  if (typeof window !== "undefined") {
    window.localStorage.setItem("roleToggleEligible", String(nextEligibility));
  }

  return nextEligibility;
};

export const canCurrentUserToggleRole = ({ user, role } = {}) => {
  const roleNames = getRoleNamesFromUser(user);

  return (
    ls.get("roleToggleEligible") === true ||
    role === "Super Admin" ||
    roleNames.includes("Super Admin") ||
    Number(user?.is_role_toggled) === 1
  );
};

export function getTodayDateString() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function clearClientAuthState(broadcast = true) {
  ls.set("authToken", false);
  ls.set("isActive", false);
  ls.set("roles", false);
  ls.set("user", false);
  ls.set("userAccess", []);

  if (typeof window !== "undefined") {
    try {
      const existingSnapshot = JSON.parse(window.localStorage.getItem("ems_timer_snapshot") || "{}");
      const today = getTodayDateString();
      const preservedTotal = Number(existingSnapshot?.totaltimer) || 0;

      window.localStorage.removeItem("authToken");
      window.localStorage.removeItem("roles");
      window.localStorage.removeItem("user");
      window.localStorage.removeItem("userAccess");
      window.localStorage.removeItem("tokenRefreshed");
      window.localStorage.removeItem("ems_login_date");
      window.localStorage.removeItem("ems_login_timestamp");
      window.localStorage.removeItem("ems_auto_timer_session_started_at");

      window.localStorage.setItem("ems_timer_snapshot", JSON.stringify({
        timer: 0,
        totaltimer: preservedTotal,
        isActive: false,
        savedAt: Date.now(),
        dateKey: today,
      }));

      window.sessionStorage.removeItem("ems_session_active");
      window.sessionStorage.removeItem("ems_session_date");
    } catch (err) {}

    if (broadcast && sessionSyncChannel) {
      try {
        sessionSyncChannel.postMessage({ type: "SESSION_FORCE_LOGOUT" });
      } catch (e) {}
    }
  }
}

let sessionSyncChannel = null;
if (typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined") {
  try {
    sessionSyncChannel = new BroadcastChannel("ems_tab_session_channel");
    sessionSyncChannel.onmessage = (event) => {
      const { type, date } = event?.data || {};
      const today = getTodayDateString();
      if (type === "SESSION_PING") {
        const isActive = window.sessionStorage.getItem("ems_session_active") === "true";
        const sessDate = window.sessionStorage.getItem("ems_session_date");
        if (isActive && sessDate === today) {
          sessionSyncChannel.postMessage({ type: "SESSION_PONG", date: today });
        }
      } else if (type === "SESSION_PONG") {
        if (date === today) {
          window.sessionStorage.setItem("ems_session_active", "true");
          window.sessionStorage.setItem("ems_session_date", today);
        }
      } else if (type === "SESSION_FORCE_LOGOUT") {
        clearClientAuthState(false);
        if (window.location && window.location.pathname !== "/login") {
          window.location.replace("/login");
        }
      }
    };

    // If this tab doesn't have an active session marker yet, ask other open tabs
    const hasActiveSess = window.sessionStorage.getItem("ems_session_active") === "true";
    const sessDate = window.sessionStorage.getItem("ems_session_date");
    if (!hasActiveSess || sessDate !== getTodayDateString()) {
      sessionSyncChannel.postMessage({ type: "SESSION_PING" });
    }
  } catch (e) {}
}

export const recordLoginSession = () => {
  const today = getTodayDateString();
  const now = Date.now().toString();
  if (typeof window !== "undefined") {
    try {
      window.sessionStorage.setItem("ems_session_active", "true");
      window.sessionStorage.setItem("ems_session_date", today);
      window.localStorage.setItem("ems_login_date", today);
      window.localStorage.setItem("ems_login_timestamp", now);
    } catch (e) {}
  }
};


export const isLoginSessionExpired = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const rawToken = ls.get("authToken") || window.localStorage.getItem("authToken");
  const hasToken = Boolean(
    rawToken &&
    rawToken !== false &&
    rawToken !== "false" &&
    rawToken !== "null" &&
    rawToken !== "undefined" &&
    String(rawToken).trim() !== ""
  );

  if (!hasToken) {
    return true;
  }

  const today = getTodayDateString();
  const storedLoginDate = window.localStorage.getItem("ems_login_date");

  // If login date is recorded and doesn't match today -> overnight passed / tomorrow ("kale")
  if (storedLoginDate && storedLoginDate !== today) {
    return true;
  }

  // If stored login date is today and token exists:
  // Ensure sessionStorage has active flag for this tab as well
  if (storedLoginDate === today) {
    const sessionActive = window.sessionStorage.getItem("ems_session_active");
    if (sessionActive !== "true") {
      window.sessionStorage.setItem("ems_session_active", "true");
      window.sessionStorage.setItem("ems_session_date", today);
    }
    return false;
  }

  // If there was no storedLoginDate yet, but user is currently authenticated (legacy session):
  // Initialize today as login date so active session continues safely
  if (!storedLoginDate) {
    window.localStorage.setItem("ems_login_date", today);
    window.sessionStorage.setItem("ems_session_active", "true");
    window.sessionStorage.setItem("ems_session_date", today);
    return false;
  }

  return false;
};
